import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth-context";
import { Sparkles, ChevronDown, X, Eye } from "lucide-react";

const STORAGE_KEY = "nodera-demo-tour-dismissed";

export default function DemoBanner() {
  const { user } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  // Show the one-time welcome card the first time a demo user lands.
  useEffect(() => {
    if (!user?.is_demo) return;
    if (typeof window === "undefined") return;
    const dismissed = window.sessionStorage.getItem(STORAGE_KEY);
    if (!dismissed) setShowOnboarding(true);
  }, [user?.is_demo]);

  // Prefix the browser tab title with [DEMO] so admins previewing the demo
  // portal in a new tab can distinguish it from their admin tab at a glance.
  useEffect(() => {
    if (typeof document === "undefined") return;
    if (!user?.is_demo) return;
    const original = document.title;
    if (!original.startsWith("[DEMO]")) {
      document.title = `[DEMO] ${original}`;
    }
    return () => {
      // Restore on unmount / when demo session ends.
      if (document.title.startsWith("[DEMO] ")) {
        document.title = document.title.replace(/^\[DEMO\]\s*/, "");
      }
    };
  }, [user?.is_demo]);

  const dismissOnboarding = () => {
    setShowOnboarding(false);
    try {
      window.sessionStorage.setItem(STORAGE_KEY, "1");
    } catch {/* ignore */}
  };

  if (!user?.is_demo) return null;

  return (
    <>
      {/* Fixed top-right pill — persistent visual cue that this tab is a demo session. */}
      <div
        data-testid="demo-session-pill"
        className="fixed top-3 right-3 z-[60] pointer-events-none select-none"
      >
        <div className="inline-flex items-center gap-1.5 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-3 py-1 text-[11px] font-bold shadow-lg shadow-blue-900/20 ring-2 ring-white/40">
          <Eye className="w-3 h-3" />
          DEMO SESSION
        </div>
      </div>

      <button
        type="button"
        data-testid="demo-banner"
        onClick={() => setExpanded((v) => !v)}
        className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2 text-xs flex items-center justify-center gap-2 shadow-sm hover:from-blue-700 hover:to-indigo-700 transition"
      >
        <Sparkles className="w-3 h-3" />
        <span className="font-semibold">DEMO MODE</span>
        <span className="opacity-90 hidden sm:inline">
          — sandbox for {user.organization || user.clinic_name}. Workflows auto-advance every 60s. Nothing leaves this environment.
        </span>
        <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? "rotate-180" : ""}`} />
      </button>
      {expanded && (
        <div
          data-testid="demo-banner-details"
          className="bg-blue-50 border-b border-blue-200 px-6 py-4 text-xs text-blue-900 leading-relaxed"
        >
          <div className="max-w-3xl mx-auto grid sm:grid-cols-3 gap-4">
            <div>
              <div className="font-semibold mb-1">⏱ Self-driving</div>
              In-flight workflows auto-advance one status step every 60 seconds. You can also press <b>Simulate vendor response</b> to advance immediately.
            </div>
            <div>
              <div className="font-semibold mb-1">🔒 Fully isolated</div>
              Demo data lives in its own slice — production clinics never see it. No real emails, SMS, or payments are triggered.
            </div>
            <div>
              <div className="font-semibold mb-1">🧪 5 demo vendors</div>
              One curated vendor per category (Sleep, Metabolic, Hormone, Diagnostics, Wellness) so you can complete the full submit → fulfill → invoice → paid loop.
            </div>
          </div>
        </div>
      )}

      {showOnboarding && (
        <div
          data-testid="demo-onboarding-modal"
          className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
          onClick={dismissOnboarding}
        >
          <div
            className="bg-white rounded-2xl border border-slate-200 w-full max-w-md p-6 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={dismissOnboarding}
              className="absolute top-3 right-3 p-1 rounded-md hover:bg-slate-100 text-slate-400"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center text-white mb-3">
              <Sparkles className="w-5 h-5" />
            </div>
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-blue-700">A few things to know</div>
            <h2 className="font-display text-lg font-bold text-slate-900 mt-1">Welcome to the Nodera demo</h2>
            <p className="text-sm text-slate-600 mt-2 leading-relaxed">
              You're inside a fully-functional clinic portal — submit workflow requests, watch them progress through the vendor queue, review invoices, render payments. Everything is sandboxed.
            </p>
            <ul className="mt-4 space-y-2 text-xs text-slate-700">
              <li className="flex gap-2">
                <span className="text-blue-600 font-bold mt-0.5">⏱</span>
                <span>
                  <b>Workflows auto-advance.</b> Once you submit, the status will progress one step every 60 seconds (Pending Vendor Acceptance → Vendor Accepted → … → Paid) so the demo behaves like a live network.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-blue-600 font-bold mt-0.5">⚡</span>
                <span>
                  <b>Want to skip ahead?</b> The "Simulate vendor response" button on each workflow advances the next step instantly.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-blue-600 font-bold mt-0.5">🔒</span>
                <span>
                  <b>Nothing leaves the demo.</b> No real emails, SMS, or payments fire. Demo data is invisible to production clinics and admins.
                </span>
              </li>
              <li className="flex gap-2">
                <span className="text-blue-600 font-bold mt-0.5">🧪</span>
                <span>
                  <b>5 vendors are pre-loaded</b> — one for each program category (Sleep, Metabolic, Hormone, Diagnostics, Wellness).
                </span>
              </li>
            </ul>
            <button
              onClick={dismissOnboarding}
              data-testid="demo-onboarding-dismiss"
              className="w-full mt-5 bg-slate-900 text-white rounded-full px-4 py-2.5 text-sm font-semibold hover:bg-slate-800"
            >
              Start exploring
            </button>
          </div>
        </div>
      )}
    </>
  );
}
